import { Injectable } from "@angular/core";

import { ApiGatewayService } from "../../api-gateway/api-gateway.service";
import { QRCodeResult } from "../../../models";
import { AuthenticationService } from "../authentication.service";
import { LoggedInUser } from "../user";
import { isCognitoUser } from "../cognito-user";

import * as QRCode from "qrcode";

const UNAUTHENTICATED_MESSAGE = "User is not logged in";
const NOT_COGNITO_MESSAGE = "User is not a cognito user";
const NEED2VERIFY_MESSAGE = "MFA Auth needs to be verified";
const MFA_NOT_INITIATED_MESSAGE = "User has not initiated mfa setup.";
const MFA_NOT_SETUP_MESSAGE = "Mfa is not setup for user";

@Injectable()
export class MfaSettingsService {
	private auth: AuthenticationService;
	private apiGatewayService: ApiGatewayService;

	constructor(
		apiGatewayService: ApiGatewayService,
		authService: AuthenticationService
	) {
		this.auth = authService;
		this.apiGatewayService = apiGatewayService;
	}

	public initiateMfaSetup(deviceName: string): Promise<QRCodeResult> {
		const user: LoggedInUser | undefined = this.auth.getCurrentUser();
		return new Promise<QRCodeResult>((resolve, reject) => {
			if (!user) {
				reject(UNAUTHENTICATED_MESSAGE);
			} else if (!isCognitoUser(user)) {
				reject(NOT_COGNITO_MESSAGE);
			} else {
				const promiseResult = user.associateSoftwareToken();
				promiseResult.then((data) => {
					const issuer = window.location.hostname;
					const url = `otpauth://totp/${encodeURIComponent(user.email)}?secret=${data.secretCode}&issuer=${encodeURIComponent(issuer)}`;
					QRCode.toDataURL(url, (error: any, dataURL: string) => {
						if (error) {
							reject(error);
						} else {
							// success, save the key only if qr code was generated
							user.mfaDevice = {
								name: deviceName,
								key: data.secretCode,
								enabled: false
							};
							this.auth.saveCurrentUser(user);
							resolve({
								result: true,
								message: NEED2VERIFY_MESSAGE,
								secretCode: data.secretCode,
								dataURL,
								tfaURL: url
							});
						}
					});
				}).catch((err: any) => {
					reject(err);
				});
			}
		});
	}

	public async completeMfaSetup(totpCode: string, deviceName: string): Promise<void> {
		const user = this.auth.getCurrentUser();
		if (!user) {
			throw new Error(UNAUTHENTICATED_MESSAGE);
		} else if (!isCognitoUser(user)) {
			throw new Error(NOT_COGNITO_MESSAGE);
		} else if (!user.mfaDevice || !user.mfaDevice.key) {
			throw new Error(MFA_NOT_INITIATED_MESSAGE);
		} else {
			await user.verifySoftwareToken(totpCode, deviceName);
			await user.confirmMfaSetup();
			user.mfaDevice.enabled = true;
			await this.apiGatewayService.updateMfaDevice(user.mfaDevice);
			// do not keep the key in store
			user.mfaDevice = {
				name: user.mfaDevice.name,
				enabled: user.mfaDevice.enabled
			};
			this.auth.saveCurrentUser(user);
		}
	}

	public async disableMfa() {
		const user = this.auth.getCurrentUser();
		if (!user) {
			throw new Error(UNAUTHENTICATED_MESSAGE);
		} else if (!isCognitoUser(user)) {
			throw new Error(NOT_COGNITO_MESSAGE);
		} else if (!user.mfaDevice) {
			throw new Error(MFA_NOT_SETUP_MESSAGE);
		} else {
			await user.disableMfa();
			// save the updated mfa settings
			user.mfaDevice = { enabled: false };
			await this.apiGatewayService.updateMfaDevice(user.mfaDevice);
			this.auth.saveCurrentUser(user);
		}
	}

	public async isMfaEnabled() {
		// fetch current mfa settings
		const userData = await this.apiGatewayService.getSelf();
		return userData.user.mfaDevice;
	}
}
